import {
  components,
  GroupBase,
  MenuListProps,
  OptionProps,
  SingleValueProps,
} from 'react-select';
import { useMemo } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import {
  PortainerSelect,
  Option,
} from '@/react/components/form-components/PortainerSelect';
import { useAuthorizations, useCurrentUser } from '@/react/hooks/useUser';
import { RegistryTypes } from '@/react/portainer/registries/types/registry';

import { FormError } from '@@/form-components/FormError';
import { FormControl, Size } from '@@/form-components/FormControl';
import { Link } from '@@/Link';
import { TextTip } from '@@/Tip/TextTip';
import { MenuPortalProps } from '@@/form-components/ReactSelect.types';

import { RegistryIcon } from './RegistryIcon';

export type RegistryValue = {
  registryId?: number; // set for OCI helm repos
  registryUrl?: string; // set for traditional https helm repos
  isOCI?: boolean;
  name?: string;
  type?: RegistryTypes;
  projectPath?: string;
};

interface Props {
  selectedRegistry: RegistryValue | null;
  onRegistryChange: (registry: RegistryValue | null) => void;
  namespace?: string;
  placeholder?: string;
  'data-cy'?: string;
  isRegistryAvailable: boolean;
  isLoading: boolean;
  isLoadingError: boolean;
  registryOptions: GroupBase<Option<RegistryValue>>[];
  formError?: string;
  size?: Size;
}

export function HelmRegistrySelect({
  selectedRegistry,
  onRegistryChange,
  namespace,
  placeholder = 'Select a repository/registry',
  'data-cy': dataCy = 'helm-registry-select',
  isRegistryAvailable,
  isLoading,
  isLoadingError,
  registryOptions,
  formError,
  size = 'small',
}: Props) {
  const { authorized: canUpdateRegistry } = useAuthorizations(
    'K8sRegistrySecretUpdate',
    useEnvironmentId(),
    true
  );
  const { isPureAdmin } = useCurrentUser();
  const hasNoOCIRegistries = useMemo(
    () =>
      registryOptions.every(
        (option) => !option.options.some((o) => o.value.isOCI)
      ),
    [registryOptions]
  );

  return (
    <FormControl
      label="Helm chart source"
      tooltip={
        <HelmChartSourceTooltip
          isPureAdmin={isPureAdmin}
          canUpdateRegistry={canUpdateRegistry}
        />
      }
      required
      size={size}
      errors={
        formError || (isLoadingError && 'Unable to load registry options.')
      }
    >
      <PortainerSelect<RegistryValue>
        placeholder={placeholder}
        value={selectedRegistry ?? {}}
        options={registryOptions}
        isLoading={isLoading}
        onChange={onRegistryChange}
        isClearable
        bindToBody
        filterOption={(option, inputValue) =>
          !!option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
          !!option.data.value.registryUrl
            ?.toLowerCase()
            .includes(inputValue.toLowerCase())
        }
        components={{
          Option: RegistryOption,
          SingleValue: RegistrySingleValue,
          MenuList: RegistryMenuList,
          MenuPortal: RegistryMenuPortal,
        }}
        data-cy={dataCy}
        noOptionsMessage={() => 'No registries available.'}
      />
      <RegistryAccessError
        isRegistryAvailable={isRegistryAvailable}
        isLoading={isLoading}
        namespace={namespace}
        selectedRegistry={selectedRegistry}
        canUpdateRegistry={canUpdateRegistry}
      />
      <NoOCIRegistriesWarning
        hasNoOCIRegistries={hasNoOCIRegistries}
        isLoading={isLoading}
        namespace={namespace}
        canUpdateRegistry={canUpdateRegistry}
        isPureAdmin={isPureAdmin}
        isRegistryAvailable={isRegistryAvailable}
      />
    </FormControl>
  );
}

function HelmChartSourceTooltip({
  isPureAdmin,
  canUpdateRegistry,
}: {
  isPureAdmin: boolean;
  canUpdateRegistry: boolean;
}) {
  if (isPureAdmin) {
    return (
      <>
        <p>
          Select the <b>registry</b> or <b>repository</b> where the Helm chart
          is located.
        </p>
        <p>
          <b>Registries</b> are defined in the{' '}
          <Link
            to="portainer.registries"
            data-cy="registries-view"
            target="_blank"
          >
            registries view
          </Link>
          , and namespaces can be given access to them in the{' '}
          <Link
            to="kubernetes.registries"
            data-cy="registries-view"
            target="_blank"
          >
            environment settings
          </Link>
          .
        </p>
        <p>
          <b>Repositories</b> are defined in the{' '}
          <Link
            to="portainer.account"
            params={{ '#': 'helm-repositories' }}
            data-cy="helm-repositories-link"
          >
            User settings - Helm repositories
          </Link>
        </p>
      </>
    );
  }

  // Non-admin but can update registry scenario
  if (canUpdateRegistry) {
    return (
      <>
        <p>
          Select the <b>registry</b> or <b>repository</b> where the Helm chart
          is located.
        </p>
        <p>
          You can give the namespaces access to registries in the{' '}
          <Link
            to="kubernetes.registries.access"
            target="_blank"
            data-cy="update-registry-permissions"
          >
            registry permissions view
          </Link>
          .
        </p>
        <p>
          <b>Repositories</b> are defined in the{' '}
          <Link
            to="portainer.account"
            params={{ '#': 'helm-repositories' }}
            data-cy="helm-repositories-link"
          >
            User settings - Helm repositories
          </Link>
        </p>
      </>
    );
  }

  // Non-admin without update permissions scenario
  return (
    <>
      <p>
        Select the <b>registry</b> or <b>repository</b> where the Helm chart is
        located.
      </p>
      <p>
        Contact your administrator to define a registry and/or give namespaces
        access to more registries.
      </p>
      <p>
        <b>Repositories</b> are defined in the{' '}
        <Link
          to="portainer.account"
          params={{ '#': 'helm-repositories' }}
          data-cy="helm-repositories-link"
        >
          User settings - Helm repositories
        </Link>
      </p>
    </>
  );
}

function RegistryAccessError({
  isRegistryAvailable,
  isLoading,
  namespace,
  selectedRegistry,
  canUpdateRegistry,
}: {
  isRegistryAvailable: boolean;
  isLoading: boolean;
  namespace?: string;
  selectedRegistry: RegistryValue | null;
  canUpdateRegistry: boolean;
}) {
  if (isRegistryAvailable || isLoading) {
    return null;
  }

  return (
    <FormError>
      The selected namespace &apos;{namespace}&apos; doesn&apos;t have access to
      use this registry.{' '}
      {canUpdateRegistry ? (
        <>
          You can give the namespace access to this registry in the{' '}
          <Link
            to="kubernetes.registries.access"
            target="_blank"
            params={{ id: selectedRegistry?.registryId }}
            data-cy="update-registry-permissions"
          >
            registry permissions
          </Link>
          .
        </>
      ) : (
        <>
          Contact your administrator to give the namespace access to this
          registry.
        </>
      )}
    </FormError>
  );
}

function NoOCIRegistriesWarning({
  hasNoOCIRegistries,
  isLoading,
  namespace,
  canUpdateRegistry,
  isPureAdmin,
  isRegistryAvailable,
}: {
  hasNoOCIRegistries: boolean;
  isLoading: boolean;
  namespace?: string;
  canUpdateRegistry: boolean;
  isPureAdmin: boolean;
  isRegistryAvailable: boolean;
}) {
  // Don't show OCI warning if registry access error is already being shown
  if (!hasNoOCIRegistries || isLoading || !namespace || !isRegistryAvailable) {
    return null;
  }

  return (
    <TextTip color="blue" className="mt-2">
      The selected namespace &apos;{namespace}&apos; doesn&apos;t have access to
      any OCI registries.
      <OCIRegistryMessage
        namespace={namespace}
        canUpdateRegistry={canUpdateRegistry}
        isPureAdmin={isPureAdmin}
      />
    </TextTip>
  );
}

function OCIRegistryMessage({
  canUpdateRegistry,
  isPureAdmin,
  namespace,
}: {
  canUpdateRegistry: boolean;
  isPureAdmin: boolean;
  namespace: string;
}) {
  if (!canUpdateRegistry) {
    return (
      <>
        <br />
        Contact your administrator to give the namespace &apos;{namespace}&apos;
        access to an OCI registry.
      </>
    );
  }

  if (!isPureAdmin) {
    return (
      <>
        <br />
        You can give the namespace access to an OCI registry by managing{' '}
        <UpdateRegistryLink />.
      </>
    );
  }

  return (
    <>
      <br />
      You can{' '}
      <Link
        to="portainer.registries.new"
        target="_blank"
        data-cy="create-oci-registry"
      >
        connect to an OCI registry
      </Link>{' '}
      and/or give &apos;{namespace}&apos; access to it by managing{' '}
      <UpdateRegistryLink />.
    </>
  );
}

function UpdateRegistryLink() {
  return (
    <Link
      to="kubernetes.registries"
      target="_blank"
      data-cy="update-registry-permissions"
    >
      registry access
    </Link>
  );
}

// truncate the registry text, because some registry names are urls, which are too long
function RegistryOption(props: OptionProps<Option<RegistryValue>>) {
  const { data: registry } = props;

  return (
    <div>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <components.Option {...props} className="truncate whitespace-nowrap">
        <div
          className="flex items-center gap-x-2 whitespace-nowrap text-inherit"
          title={registry.label}
        >
          <RegistryIcon
            type={registry.value.type ?? RegistryTypes.CUSTOM}
            className="!h-6 !w-6"
          />
          <span className="truncate font-medium">{registry.label}</span>
        </div>
        {!!registry.value.registryUrl && registry.value.isOCI && (
          <div
            className="text-muted ml-8 truncate text-xs"
            title={registry.value.registryUrl}
          >
            {registry.value.registryUrl}
          </div>
        )}
      </components.Option>
    </div>
  );
}

function RegistrySingleValue(
  props: SingleValueProps<
    Option<RegistryValue>,
    false,
    GroupBase<Option<RegistryValue>>
  >
) {
  const { data: registry } = props;

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.SingleValue {...props}>
      <div className="flex items-center gap-x-2">
        <RegistryIcon
          type={registry.value.type ?? RegistryTypes.CUSTOM}
          className="!h-4 !w-4"
        />
        <span className="truncate">{registry.label}</span>
      </div>
    </components.SingleValue>
  );
}

// make the list bigger to have more space to browse through the registries
function RegistryMenuList(props: MenuListProps<Option<RegistryValue>>) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.MenuList {...props} className="!max-h-[600px]" />
  );
}

// ensure the menu is rendered above the upgrade modal
function RegistryMenuPortal(
  props: MenuPortalProps<
    Option<RegistryValue>,
    false,
    GroupBase<Option<RegistryValue>>
  >
) {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.MenuPortal {...props} className="!z-50" />
  );
}
